import React, {useCallback, useEffect, useState} from 'react'
import {Helmet} from 'react-helmet'

export interface Breadcrumb {
  title: string
  url?: string
}

export interface BreadcrumbContextProps {
  breadcrumbs: Breadcrumb[]
  set: (breadcrumbs: Breadcrumb[]) => void
  clear: () => void
}

const BreadcrumbContext = React.createContext<
  BreadcrumbContextProps | undefined
>(undefined)

export default function BreadcrumbProvider(props: {children: React.ReactNode}) {
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([])

  const clearBreadcrumbs = useCallback(() => setBreadcrumbs([]), [])

  return (
    <BreadcrumbContext.Provider
      value={{breadcrumbs, set: setBreadcrumbs, clear: clearBreadcrumbs}}
    >
      <BreadcrumbTitle breadcrumbs={breadcrumbs} />
      {props.children}
    </BreadcrumbContext.Provider>
  )
}

function BreadcrumbTitle(props: {breadcrumbs: Breadcrumb[]}) {
  const titles = props.breadcrumbs
    .map((breadcrumb) => breadcrumb.title)
    .filter(Boolean)
    .reverse()

  if (titles.length === 0) {
    return <Helmet defaultTitle="Obv.io" />
  }

  return (
    <Helmet>
      <title>{`${titles.join(' · ')} | Obv.io`}</title>
    </Helmet>
  )
}

export function useBreadcrumbs(
  breadcrumbs?: Breadcrumb[],
  dependencies: string[] = [],
) {
  const context = React.useContext(BreadcrumbContext)
  const set = context?.set
  const clear = context?.clear

  useEffect(() => {
    if (!breadcrumbs || !set || !clear) {
      return
    }
    set(breadcrumbs)

    // Clear when navigating to another page
    return clear

    /**
     * Only want to set on first call to let last call win. If we
     * re-set on every breadcrumbs change, we would get a loop
     * when a parent, and child both set the breadcrumbs.
     */
    // eslint-disable-next-line
  }, dependencies)

  if (context === undefined) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbProvider')
  }

  return context
}

/**
 * Helper component that will set breadcrumbs. Note that components are rendered
 * inside-out, and last 'set' wins with breadcrumbs, so any breadcrumbs set in
 * children components WILL be overwritten.
 *
 * @param props
 * @returns
 */
export function WithBreadcrumbs(props: {
  children: JSX.Element | JSX.Element[]
  breadcrumbs: Breadcrumb[]
}) {
  useBreadcrumbs(props.breadcrumbs)

  return <>{props.children}</>
}
